/**
 * Payments Service — Stripe-based per-org subscriptions.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:14102/api/v1';
const API_BASE_URL = BASE_URL.replace(/\/api\/v1\/?$/, '');
const ACCESS_TOKEN_KEY = 'access_token';

function authHeader(): { Authorization: string } | undefined {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

export type BillingInterval = 'month' | 'year';
export type SubscriptionStatus =
  | 'inactive'
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid';
export type PaymentStatus =
  | 'pending'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'void'
  | 'uncollectible'
  | 'expired';

export interface PaymentPlan {
  id: number;
  name: string;
  description: string | null;
  stripe_price_id: string | null;
  amount_cents: number;
  currency: string;
  billing_interval: BillingInterval;
  is_active: boolean;
  sort_order: number;
}

export interface OrgSubscription {
  id: number;
  organization_id: number;
  plan_id: number | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: SubscriptionStatus;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
}

export interface Invoice {
  id: number;
  organization_id: number;
  stripe_invoice_id: string | null;
  amount_cents: number;
  currency: string;
  status: PaymentStatus;
  description: string | null;
  hosted_invoice_url: string | null;
  receipt_url: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface PaymentsConfig {
  stripe_configured: boolean;
  webhook_configured: boolean;
}

export const paymentsService = {
  async getConfig(): Promise<PaymentsConfig> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/payments/config`, { headers: authHeader() });
    return res.data;
  },
  async listPlans(includeInactive = false): Promise<PaymentPlan[]> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/payments/plans`, {
      headers: authHeader(),
      params: { all: includeInactive ? 'true' : undefined },
    });
    return res.data;
  },
  async createPlan(dto: Partial<PaymentPlan> & { name: string; amount_cents: number; billing_interval: BillingInterval }): Promise<PaymentPlan> {
    const res = await axios.post(`${API_BASE_URL}/api/v1/payments/plans`, dto, { headers: authHeader() });
    return res.data;
  },
  async getSubscription(): Promise<{ subscription: OrgSubscription | null; plan: PaymentPlan | null }> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/payments/subscription`, { headers: authHeader() });
    return res.data;
  },
  async subscribe(planId: number): Promise<{ checkout_url: string; session_id: string }> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/payments/subscribe`,
      { plan_id: planId },
      { headers: authHeader() },
    );
    return res.data;
  },
  async openPortal(): Promise<string> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/payments/portal`,
      {},
      { headers: authHeader() },
    );
    return res.data.portal_url;
  },
  async cancel(): Promise<OrgSubscription> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/payments/cancel`,
      {},
      { headers: authHeader() },
    );
    return res.data;
  },
  async resume(): Promise<OrgSubscription> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/payments/resume`,
      {},
      { headers: authHeader() },
    );
    return res.data;
  },
  async listInvoices(): Promise<Invoice[]> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/payments/invoices`, { headers: authHeader() });
    return res.data;
  },
};

export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}
